import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { CognitoUser, CognitoUserPool, CognitoUserSession, CognitoUserAttribute, AuthenticationDetails } from 'amazon-cognito-identity-js';

// reducer - state management
import { CHANGE_PASSWORD, LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import { AWS_API } from 'config';
import { AWSCognitoContextType, InitialLoginContextProps } from 'types/auth';
import { useLocation } from 'react-router-dom';

// constant
const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

export const userPool = new CognitoUserPool({
    UserPoolId: AWS_API.poolId || '',
    ClientId: AWS_API.appClientId || ''
});

const setSession = (serviceToken?: string | null) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
    } else {
        localStorage.removeItem('serviceToken');
        localStorage.clear();
    }
};

// ==============================|| AWS Cognito CONTEXT & PROVIDER ||============================== //
const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null);

export const AWSCognitoProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const location = useLocation();

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');
                if (serviceToken) {
                    setSession(serviceToken);
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: {
                                name: 'Betty'
                            }
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                console.error(err);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    useEffect(() => {
        const serviceToken = window.localStorage.getItem('serviceToken');

        if (serviceToken) {
            const expirationToken = expToken(serviceToken);
            const dateNow = Date.now();

            if (expirationToken <= dateNow) {
                logout();
            }
        } else {
            logout();
        }
    }, [location.pathname]);

    const login = async (email: string, password: string, newPassword?: string) =>
        new Promise((resolve, reject) => {
            const usr = new CognitoUser({
                Username: email,
                Pool: userPool
            });

            const authData = new AuthenticationDetails({
                Username: email,
                Password: password
            });

            usr.authenticateUser(authData, {
                onSuccess: (session: CognitoUserSession) => {
                    setSession(session.getAccessToken().getJwtToken());

                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            isChangePassword: false,
                            user: {
                                email: authData.getUsername(),
                                name: authData.getUsername()
                            }
                        }
                    });

                    return resolve(state);
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    if (newPassword) {
                        delete userAttributes.email_verified;
                        delete userAttributes.phone_number_verified;
                        delete userAttributes.email;
                        usr.completeNewPasswordChallenge(newPassword, userAttributes, requiredAttributes);

                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                isChangePassword: false,
                                user: {
                                    email: authData.getUsername(),
                                    name: authData.getUsername()
                                }
                            }
                        });

                        return resolve({ ...state, isChangePassword: false });
                    }

                    dispatch({
                        type: CHANGE_PASSWORD,
                        payload: {
                            isLoggedIn: false,
                            isChangePassword: true,
                            user: {
                                email: authData.getUsername(),
                                name: authData.getUsername()
                            }
                        }
                    });

                    return resolve({ isChangePassword: true });
                },
                onFailure(err: any): void {
                    reject(err);
                }
            });
        });

    const register = (email: string, password: string, firstName: string, lastName: string) =>
        new Promise((success, rej) => {
            userPool.signUp(
                email,
                password,
                [
                    new CognitoUserAttribute({ Name: 'email', Value: email }),
                    new CognitoUserAttribute({ Name: 'name', Value: `${firstName} ${lastName}` })
                ],
                [],
                async (err, result) => {
                    if (err) {
                        rej(err);
                        return;
                    }
                    success(result);
                }
            );
        });

    const logout = () => {
        const loggedInUser = userPool.getCurrentUser();
        if (loggedInUser) {
            localStorage.clear();
            setSession(null);
            loggedInUser.signOut();
            dispatch({ type: LOGOUT });
        }
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return <AWSCognitoContext.Provider value={{ ...state, login, logout, register }}>{children}</AWSCognitoContext.Provider>;
};

export default AWSCognitoContext;

function expToken(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('')
    );

    const parsed = JSON.parse(jsonPayload);

    return parsed.exp * 1000;
}
